/**
 * Shared navigation data — used by the sidebar (AppShell) and the
 * command palette. Single source of truth for module paths.
 *
 * Both read it through components/nav-model.ts, which decides what a role
 * sees and lists each page once in the palette. CRM pages are listed under
 * CRM only: test/crm-navigation.test.tsx fails on a copy anywhere else.
 *
 * `availability: "notYet"` marks a page whose feature runs nothing yet
 * (components/NotAvailable.tsx): the sidebar and the palette tag it "Not
 * available yet", so no entry presents it as working (REP-BUILDERS, D661).
 */
export type NavAvailability = "notYet";

export const NAV_ENTRIES: Array<{
  label: string;
  path: string;
  permission?: string;
  availability?: NavAvailability;
  children?: Array<{
    label: string;
    path: string;
    permission?: string;
    availability?: NavAvailability;
  }>;
}> = [
  { label: "Home", path: "/app/home", permission: "reporting.dashboard.read" },
  { label: "Inbox", path: "/app/inbox", permission: "approval.read" },
  {
    label: "Organization",
    path: "/app/organization",
    permission: "organization.legal_entity.read",
    children: [
      {
        label: "Structure",
        path: "/app/organization",
        permission: "organization.branch.read",
      },
      {
        label: "Legal Entities",
        path: "/app/organization/legal-entities",
        permission: "organization.legal_entity.read",
      },
      {
        label: "Companies",
        path: "/app/organization/companies",
        permission: "organization.legal_entity.read",
      },
      {
        label: "Branches",
        path: "/app/organization/branches",
        permission: "organization.company.read",
      },
      {
        label: "Departments",
        path: "/app/organization/departments",
        permission: "organization.department.read",
      },
      {
        label: "Locations",
        path: "/app/organization/locations",
        permission: "organization.branch.read",
      },
    ],
  },
  {
    label: "CRM",
    path: "/app/crm",
    permission: "crm.lead.read",
    children: [
      { label: "Overview", path: "/app/crm", permission: "crm.activity.read" },
      { label: "Leads", path: "/app/crm/leads", permission: "crm.lead.read" },
      {
        label: "Opportunities",
        path: "/app/crm/opportunities",
        permission: "crm.opportunity.read",
      },
      {
        label: "Contacts",
        path: "/app/crm/contacts",
        permission: "crm.lead.read",
      },
      {
        label: "Activities",
        path: "/app/crm/activities",
        permission: "crm.activity.read",
      },
      {
        label: "Campaigns",
        path: "/app/crm/campaigns",
        permission: "crm.campaign.read",
      },
    ],
  },
  {
    label: "Planning",
    path: "/app/planning",
    permission: "planning.forecast.read",
    children: [
      {
        label: "Overview",
        path: "/app/planning",
        permission: "planning.forecast.read",
      },
      {
        label: "Demand Forecasts",
        path: "/app/planning/forecasts",
        permission: "planning.forecast.read",
      },
      {
        label: "MRP Runs",
        path: "/app/planning/mrp",
        permission: "planning.mrp.read",
      },
    ],
  },
  {
    label: "E-commerce",
    path: "/app/ecommerce",
    permission: "ecommerce.storefront.read",
    children: [
      {
        label: "Overview",
        path: "/app/ecommerce",
        permission: "ecommerce.order.read",
      },
      {
        label: "Storefronts",
        path: "/app/ecommerce/storefronts",
        permission: "ecommerce.storefront.read",
      },
      {
        label: "Product Feeds",
        path: "/app/ecommerce/product-feeds",
        permission: "ecommerce.feed.read",
      },
      {
        label: "Online Orders",
        path: "/app/ecommerce/orders",
        permission: "ecommerce.order.read",
      },
    ],
  },
  {
    label: "Compliance",
    path: "/app/compliance",
    permission: "compliance.calendar.read",
    children: [
      {
        label: "Overview",
        path: "/app/compliance",
        permission: "compliance.calendar.read",
      },
      {
        label: "Calendar",
        path: "/app/compliance/calendar",
        permission: "compliance.calendar.read",
      },
      {
        label: "Regulatory Reports",
        path: "/app/compliance/regulatory-reports",
        permission: "compliance.report.read",
      },
      {
        label: "E-invoicing",
        path: "/app/compliance/e-invoicing",
        permission: "compliance.einvoicing.read",
      },
      {
        label: "Audit Files",
        path: "/app/compliance/audit-files",
        permission: "compliance.audit_file.read",
      },
      {
        label: "Data Subject Requests",
        path: "/app/compliance/data-subject-requests",
        permission: "compliance.dsar.read",
      },
      {
        label: "Retention Policies",
        path: "/app/compliance/retention",
        permission: "compliance.retention.read",
      },
    ],
  },
  {
    label: "Configuration",
    path: "/app/config/governance",
    permission: "entity-definition.read",
    children: [
      {
        label: "Governance",
        path: "/app/config/governance",
        permission: "entity-definition.read",
      },
      {
        label: "Segregation of Duties",
        path: "/app/governance/sod-conflicts",
        permission: "sod-rule.manage",
      },
      {
        label: "Pending Changes",
        path: "/app/config/governance/changes",
        permission: "entity-definition.read",
      },
      {
        label: "Activated Versions",
        path: "/app/config/governance/versions",
        permission: "entity-definition.read",
      },
      {
        label: "Entity Builder",
        path: "/app/setup/entity-builder",
        permission: "entity-definition.read",
      },
      {
        label: "Form & View Designer",
        path: "/app/setup/form-designer",
        permission: "layout.read",
      },
    ],
  },
  {
    label: "Finance",
    path: "/app/finance",
    permission: "finance.read",
    children: [
      {
        label: "Financial Dashboard",
        path: "/app/finance",
        permission: "gl.read",
      },
      {
        label: "Chart of Accounts",
        path: "/app/finance/accounts",
        permission: "account.read",
      },
      {
        label: "Journal Entries",
        path: "/app/finance/journal-entries",
        permission: "journal-entry.read",
      },
      {
        label: "General Ledger",
        path: "/app/finance/general-ledger",
        permission: "gl.read",
      },
      {
        label: "Trial Balance",
        path: "/app/finance/trial-balance",
        permission: "gl.trial-balance",
      },
      {
        label: "Financial Statements",
        path: "/app/finance/financial-statements",
        permission: "gl.read",
      },
      {
        label: "Customer Invoices",
        path: "/app/finance/customer-invoices",
        permission: "sales.invoice.read",
      },
      {
        label: "Supplier Invoices",
        path: "/app/finance/supplier-invoices",
        permission: "procurement.supplier_invoice.read",
      },
      {
        label: "Payments",
        path: "/app/finance/payments",
        permission: "sales.payment.read",
      },
      {
        label: "Bank Reconciliation",
        path: "/app/finance/bank-reconciliation",
        permission: "account.read",
      },
      {
        label: "Cash Management",
        path: "/app/finance/cash-management",
        permission: "account.read",
      },
      {
        label: "Fixed Assets",
        path: "/app/finance/fixed-assets",
        permission: "account.read",
      },
      {
        label: "Tax Dashboard",
        path: "/app/finance/tax",
        permission: "tax.read",
      },
      {
        label: "Period Close",
        path: "/app/finance/period-close",
        permission: "period.read",
      },
      {
        label: "Exchange Rates",
        path: "/app/finance/exchange-rates",
        permission: "exchange-rate.read",
      },
      {
        label: "Budgeting",
        path: "/app/finance/budgeting",
        permission: "account.read",
      },
      {
        label: "Consolidation",
        path: "/app/finance/consolidation",
        permission: "gl.read",
      },
    ],
  },
  {
    label: "Sales",
    path: "/app/sales",
    permission: "sales.read",
    children: [
      // Leads and opportunities are CRM's, listed once, under CRM above. The
      // old /app/sales/leads and /app/sales/opportunities redirect there
      // (CRM-W01, docs/design/crm.md §5.5 "Retired").
      {
        label: "Quotations",
        path: "/app/sales/quotations",
        permission: "sales.quotation.read",
      },
      {
        label: "Sales Orders",
        path: "/app/sales/orders",
        permission: "sales.order.read",
      },
      {
        label: "Customers",
        path: "/app/sales/customers",
        permission: "sales.customer.read",
      },
      {
        label: "Delivery Notes",
        path: "/app/sales/delivery-notes",
        permission: "sales.delivery_note.read",
      },
      {
        label: "Sales Invoices",
        path: "/app/sales/invoices",
        permission: "sales.invoice.read",
      },
      {
        label: "Returns / RMAs",
        path: "/app/sales/returns",
        permission: "sales.return.read",
      },
      {
        label: "Price Lists & Discounts",
        path: "/app/sales/price-lists",
        permission: "sales.price_list.read",
      },
      {
        label: "Credit Limit Review",
        path: "/app/sales/credit-limit-review",
        permission: "sales.credit_limit.read",
      },
      {
        label: "Commissions",
        path: "/app/sales/commissions",
        permission: "sales.commission.read",
      },
    ],
  },
  {
    label: "Procurement",
    path: "/app/procurement",
    permission: "procurement.read",
    children: [
      {
        label: "Purchase Requests",
        path: "/app/procurement/requests",
        permission: "procurement.purchase_request.read",
      },
      {
        label: "RFQs",
        path: "/app/procurement/rfqs",
        permission: "procurement.rfq.read",
      },
      {
        label: "Purchase Orders",
        path: "/app/procurement/purchase-orders",
        permission: "procurement.purchase_order.read",
      },
      {
        label: "Goods Receipts",
        path: "/app/procurement/goods-receipts",
        permission: "procurement.goods_receipt.read",
      },
      {
        label: "Supplier Invoices",
        path: "/app/procurement/supplier-invoices",
        permission: "procurement.supplier_invoice.read",
      },
      {
        label: "Suppliers",
        path: "/app/procurement/suppliers",
        permission: "procurement.supplier.read",
      },
      {
        label: "Supplier Evaluation",
        path: "/app/procurement/supplier-evaluation",
        permission: "procurement.supplier_evaluation.read",
      },
      {
        label: "Procurement Contracts",
        path: "/app/procurement/contracts",
        permission: "contract.read",
      },
    ],
  },
  {
    label: "Inventory",
    path: "/app/inventory",
    permission: "inventory.read",
    children: [
      {
        label: "Products",
        path: "/app/inventory/products",
        permission: "inventory.product.read",
      },
      {
        label: "Stock by Location",
        path: "/app/inventory/stock",
        permission: "inventory.product.read",
      },
      {
        label: "Inventory Movements",
        path: "/app/inventory/movements",
        permission: "inventory.product.read",
      },
      {
        label: "Transfers",
        path: "/app/inventory/transfers",
        permission: "inventory.product.read",
      },
      {
        label: "Adjustments",
        path: "/app/inventory/adjustments",
        permission: "inventory.product.read",
      },
      {
        label: "Cycle Counts",
        path: "/app/inventory/cycle-counts",
        permission: "inventory.product.read",
      },
      {
        label: "Lot / Serial Traceability",
        path: "/app/inventory/traceability",
        permission: "inventory.lot.read",
      },
      {
        label: "Reorder Planner",
        path: "/app/inventory/reorder-planner",
        permission: "inventory.product.read",
      },
      {
        label: "Valuation Report",
        path: "/app/inventory/valuation",
        permission: "inventory.product.read",
      },
    ],
  },
  {
    label: "Manufacturing",
    path: "/app/manufacturing",
    permission: "manufacturing.read",
    children: [
      {
        label: "BOMs / Routings",
        path: "/app/manufacturing/boms",
        permission: "manufacturing.bom.read",
      },
      {
        label: "Work Centers",
        path: "/app/manufacturing/work-centers",
        permission: "manufacturing.work_center.read",
      },
      {
        label: "Production Orders",
        path: "/app/manufacturing/production-orders",
        permission: "manufacturing.production_order.read",
      },
      {
        label: "MRP Planner",
        path: "/app/manufacturing/mrp",
        permission: "planning.mrp.read",
      },
      {
        label: "Quality Inspections",
        path: "/app/manufacturing/quality-inspections",
        permission: "quality.inspection.read",
      },
    ],
  },
  {
    label: "HR & Payroll",
    path: "/app/hr",
    permission: "hr.read",
    children: [
      {
        label: "Employee Directory",
        path: "/app/hr/employees",
        permission: "hr.employee.read",
      },
      {
        label: "Leave Requests",
        path: "/app/hr/leave",
        permission: "hr.leave.read",
      },
      {
        label: "Time & Attendance",
        path: "/app/hr/time-attendance",
        permission: "hr.attendance.read",
      },
      {
        label: "Payroll Runs",
        path: "/app/hr/payroll-runs",
        permission: "payroll.pay_run.read",
      },
      {
        label: "Payslips",
        path: "/app/hr/payslips",
        permission: "payroll.payslip.read",
      },
    ],
  },
  { label: "Projects", path: "/app/projects", permission: "project.read" },
  {
    label: "Service Tickets",
    path: "/app/services/tickets",
    permission: "service.read",
  },
  { label: "POS", path: "/app/pos/sessions", permission: "pos.read" },
  {
    label: "Shipments",
    path: "/app/logistics/shipments",
    permission: "logistics.read",
  },
  {
    label: "Fleet Vehicles",
    path: "/app/logistics/fleet",
    permission: "fleet.read",
  },
  { label: "Documents", path: "/app/documents", permission: "document.read" },
  {
    label: "Reports & BI",
    path: "/app/reports",
    permission: "reporting.read",
    children: [
      {
        label: "Dashboard Builder",
        path: "/app/reports/dashboard-builder",
        permission: "reporting.dashboard.read",
        availability: "notYet",
      },
      {
        label: "Report Builder",
        path: "/app/reports/builder",
        permission: "reporting.read",
        availability: "notYet",
      },
      {
        label: "Pivot Report Viewer",
        path: "/app/reports/pivot",
        permission: "reporting.pivot.read",
        availability: "notYet",
      },
    ],
  },
  {
    label: "Setup",
    path: "/app/setup",
    permission: "implementation.manage",
    children: [
      { label: "Setup Center", path: "/app/setup" },
      {
        label: "Implementation Wizard",
        path: "/app/setup/wizard",
        permission: "implementation.manage",
      },
      {
        label: "Audit Log Explorer",
        path: "/app/setup/audit-log",
        permission: "audit.log.read",
      },
      {
        label: "API / Webhook Admin",
        path: "/app/setup/api-webhooks",
        permission: "api-client.read",
      },
      {
        label: "User Management",
        path: "/app/setup/users",
        permission: "user.read",
      },
      {
        label: "Roles & Permissions",
        path: "/app/setup/roles",
        permission: "role.read",
      },
      {
        label: "Workflow Builder",
        path: "/app/setup/workflows",
        permission: "workflow.read",
      },
      {
        label: "Approval Matrix Designer",
        path: "/app/setup/approval-matrix",
        permission: "approval-matrix.read",
      },
      {
        label: "Entity Builder",
        path: "/app/setup/entity-builder",
        permission: "entity-definition.read",
      },
      {
        label: "Form / View Designer",
        path: "/app/setup/form-designer",
        permission: "layout.read",
      },
      {
        label: "Country Pack Manager",
        path: "/app/setup/country-packs",
        permission: "pack.read",
      },
      {
        label: "Industry Pack Manager",
        path: "/app/setup/industry-packs",
        permission: "pack.read",
      },
      {
        label: "Integration Center",
        path: "/app/setup/integrations",
        permission: "integration.connector.read",
      },
      {
        label: "Data Import Center",
        path: "/app/setup/data-import",
        permission: "import.read",
      },
      {
        label: "Tenant Settings",
        path: "/app/setup/tenant",
        permission: "tenant.settings.read",
      },
      {
        label: "Security Center",
        path: "/app/setup/security",
        permission: "audit.log.read",
      },
      {
        label: "Billing / Subscription",
        path: "/app/setup/billing",
        permission: "billing.read",
      },
    ],
  },
  {
    label: "Marketplace",
    path: "/app/marketplace",
    permission: "marketplace.read",
  },
  { label: "Notifications", path: "/app/notifications" },
  { label: "Search", path: "/app/search", permission: "search.read" },
  { label: "Profile", path: "/app/profile", permission: "user.read" },
];
